import { TypedDocumentNode, gql } from '@apollo/client';
import qs from 'qs';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Position, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { DefaultPosition } from '@sorare/core/src/assets/icons/DefaultPosition';
import { LinkBox, LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { isType } from '@sorare/core/src/gql';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { arrayToObject, range } from '@sorare/core/src/lib/arrays';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { getSortedAppearancesFromRules } from '@sorare/core/src/lib/players';

import { AppearanceRuleIcon } from '@sorare/gameplay/src/components/lineup/AppearanceRuleIcon';
import { LineupStyle } from '@sorare/gameplay/src/components/lineup/LineupStyleSwitch';
import { PlayerCard } from '@sorare/gameplay/src/components/lineup/PlayerCard';
import { SimpleCardPlaceholder } from '@sorare/gameplay/src/components/lineup/SimpleCardPlaceholder';
import { So5AppearanceDialog } from '@sorare/gameplay/src/components/stats/So5AppearanceDialog';
import { useComposeFlowFF } from '@sorare/gameplay/src/hooks/compose/useComposeFlowFF';
import {
  getComposeTeamRoute,
  isAppearanceLocked,
  isFixtureLive,
} from '@sorare/gameplay/src/lib/so5';

import { CardsLayout } from 'components/lineup/CardsLayout';

import { NbaSo5Lineup_so5LeaderboardContender } from './__generated__/index.graphql';

const Slot = styled(LinkBox)`
  margin: 0 auto auto;
  width: 100%;
  height: 100%;
  min-width: 0;
`;
const SlotFooter = styled.div`
  display: grid;
  grid-template-rows: var(--triple-unit) var(--double-and-a-half-unit);
  gap: var(--half-unit);
  justify-items: center;
`;

type Props = {
  so5LeaderboardContender?: NbaSo5Lineup_so5LeaderboardContender;
  lineupStyle?: LineupStyle;
  withInlineGamesDots?: boolean;
  className?: string;
  fillHeightPercent?: number;
  displayLiveEdit?: boolean;
};

export const So5Lineup = ({
  so5LeaderboardContender,
  lineupStyle = 'spread',
  withInlineGamesDots = false,
  className,
  fillHeightPercent,
  displayLiveEdit,
}: Props) => {
  const {
    flags: { useEligibleCardsCount = false },
  } = useFeatureFlags();
  const useComposeFlow20 = useComposeFlowFF();
  const [so5Appearance, setSo5Appearance] = useState<
    | NonNullable<
        NbaSo5Lineup_so5LeaderboardContender['so5Lineup']
      >['so5Appearances'][0]
    | undefined
  >();
  const navigate = useNavigate();

  const { formatMessage } = useIntl();
  const { so5Lineup, so5Leaderboard, managerTeam } =
    so5LeaderboardContender || {};

  const appearances =
    so5Leaderboard &&
    getSortedAppearancesFromRules({
      so5Lineup: so5Lineup || undefined,
      so5Leaderboard,
    });
  const card =
    so5Appearance && 'anyCard' in so5Appearance
      ? so5Appearance.anyCard
      : undefined;

  const eligibleCardsCountByPosition = arrayToObject({
    value: c => c,
    key: c => c.position,
    list: so5Leaderboard?.eligibleCardsCountByPosition || [],
  }) as Record<
    Position,
    NbaSo5Lineup_so5LeaderboardContender['so5Leaderboard']['eligibleCardsCountByPosition'][number]
  >;

  const canLiveEditLineup =
    displayLiveEdit &&
    isFixtureLive(so5Leaderboard?.so5Fixture) &&
    so5Leaderboard?.canCompose?.value &&
    !appearances?.every(
      a => isType(a, 'So5Appearance') && isAppearanceLocked(a)
    );

  return (
    <>
      <CardsLayout
        fillHeightPercent={fillHeightPercent}
        inline={lineupStyle === 'inline'}
        slots={appearances?.length}
        className={className}
      >
        {appearances?.map((appearance, i) => {
          const appearanceRule = so5Leaderboard?.rules?.appearances?.at(i);

          if (!appearanceRule) {
            return null;
          }

          const position =
            (appearanceRule.positions.length === 1 &&
              appearanceRule.positions[0]) ||
            null;

          const count =
            position && eligibleCardsCountByPosition[position]
              ? eligibleCardsCountByPosition[position].totalCount -
                eligibleCardsCountByPosition[position].usedCardsCount
              : undefined;

          const composeTeamPath = so5Leaderboard?.slug
            ? getComposeTeamRoute({
                leaderboardSlug: so5Leaderboard?.slug,
                eventType: so5Leaderboard?.so5Fixture.type.toLowerCase(),
                managerTeamId: managerTeam?.id,
                lineupId: so5Lineup?.id,
                sport: Sport.NBA,
                useComposeFlow20,
              })
            : '';

          return (
            // eslint-disable-next-line react/no-array-index-key
            <Slot key={i}>
              {'id' in appearance ? (
                <PlayerCard
                  appearance={appearance}
                  showScore
                  withDate={lineupStyle === 'spread'}
                  withInlineGamesDots={withInlineGamesDots}
                  onClick={() => {
                    setSo5Appearance(appearance);
                  }}
                  Footer={SlotFooter}
                  displayLiveEdit={canLiveEditLineup}
                  editPath={composeTeamPath}
                />
              ) : (
                <LinkOther
                  as={Link}
                  aria-label={formatMessage(fantasy.composeLineup)}
                  to={getComposeTeamRoute({
                    leaderboardSlug: so5Leaderboard?.slug || '',
                    lineupId: so5Lineup?.id,
                    managerTeamId: managerTeam?.id,
                    sport: Sport.NBA,
                    useComposeFlow20,
                    eventType: so5Leaderboard?.so5Fixture.type.toLowerCase(),
                  })}
                >
                  <SimpleCardPlaceholder
                    icon={
                      <AppearanceRuleIcon appearanceRule={appearanceRule} />
                    }
                    label={appearanceRule.shortName || ''}
                    count={useEligibleCardsCount ? count : undefined}
                    loading={!so5LeaderboardContender}
                    Footer={SlotFooter}
                  />
                </LinkOther>
              )}
            </Slot>
          );
        }) ||
          range(5).map(i => (
            <Slot key={i}>
              <SimpleCardPlaceholder
                icon={<DefaultPosition />}
                label=""
                Footer={SlotFooter}
                loading={false}
              />
            </Slot>
          ))}
      </CardsLayout>
      {so5Appearance && (
        <So5AppearanceDialog
          open
          onClose={() => setSo5Appearance(undefined)}
          showUpsell={!card}
          so5AppearanceId={so5Appearance.id}
          onClickGame={id =>
            navigate(
              `?${qs.stringify({
                [DialogKey.game]: id,
                [DialogKey.eventType]:
                  so5LeaderboardContender?.so5Leaderboard.so5Fixture.type,
              })}`
            )
          }
        />
      )}
    </>
  );
};

So5Lineup.fragments = {
  so5LeaderboardContender: gql`
    fragment NbaSo5Lineup_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        so5Appearances {
          id
          position
          so5Score {
            id
          }
          anyCard {
            slug
          }
          ...PlayerCard_so5Appearance
        }
        ...getSortedAppearancesFromRules_so5Lineup
      }
      managerTeam {
        id
      }
      so5Leaderboard {
        slug
        mainRarityType
        canCompose {
          value
        }
        so5Fixture {
          slug
          type
          ...isFixtureLive_so5Fixture
        }
        rules {
          id
          appearances {
            name
            positions
            shortName
            ...AppearanceRuleIcon_appearanceRule
          }
        }
        eligibleCardsCountByPosition {
          slug
          position
          totalCount
          usedCardsCount
        }
        ...getSortedAppearancesFromRules_so5Leaderboard
      }
    }
    ${PlayerCard.fragments.so5Appearance}
    ${getSortedAppearancesFromRules.fragments.so5Lineup}
    ${getSortedAppearancesFromRules.fragments.so5Leaderboard}
    ${AppearanceRuleIcon.fragments.appearanceRule}
    ${isFixtureLive.fragments.so5Fixture}
  ` as TypedDocumentNode<NbaSo5Lineup_so5LeaderboardContender>,
};
